import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Timer, QrCode, Sparkle, Star, Certificate } from "@phosphor-icons/react";

const PLANS = [
  {
    key: "basic",
    name: "Basic",
    price: "₹1,499",
    period: "/month",
    students: "Up to 100 Students",
    features: ["Standard QR Codes", "Email Support"],
    cta: "Choose Basic",
    featured: false,
  },
  {
    key: "pro",
    name: "Pro",
    badge: "Best Value",
    price: "₹3,999",
    period: "/month",
    students: "Up to 500 Students",
    features: ["Custom Branding", "Priority Support", "Standard QR Codes"],
    cta: "Choose Pro",
    featured: true,
  },
  {
    key: "institutional",
    name: "Institutional",
    price: "₹7,999",
    period: "/month",
    students: "Up to 2,000 Students",
    features: ["Advanced Proctoring", "Dedicated Manager", "Custom Branding"],
    cta: "Choose Institutional",
    featured: false,
  },
];

const FEATURES = [
  { icon: QrCode, title: "Instant QR Verification", body: "One scan to verify authenticity. No more fake certificates.", testid: "poster-feat-qr" },
  { icon: Timer, title: "Automated Exams", body: "Upload your questions and let the system handle the rest.", testid: "poster-feat-exams" },
  { icon: Certificate, title: "Professional Branding", body: "Your logo, your signature, your prestige — on every certificate.", testid: "poster-feat-brand" },
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="pricing-page">
      {/* Top bar */}
      <header className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-nav-home">
            <div className="font-display text-xl font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-l border-zinc-200 pl-3 ml-1">
              Examination Platform v1.0
            </span>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-block text-sm font-medium px-4 py-2 text-zinc-700 hover:text-zinc-900" data-testid="pricing-nav-back">Home</Link>
            <Link to="/login" className="text-sm font-medium px-4 py-2 text-zinc-700 hover:text-zinc-900" data-testid="pricing-nav-login">Sign in</Link>
            <Link to="/register" className="text-sm font-medium px-4 py-2 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors" data-testid="pricing-nav-register">
              Get started
            </Link>
          </nav>
        </div>
      </header>

      {/* Poster — Pricing & value */}
      <section id="pricing" className="border-b border-zinc-200 bg-zinc-950 text-zinc-100 relative overflow-hidden" data-testid="poster-section">
        {/* Grid backdrop */}
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)", backgroundSize: "48px 48px" }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          {/* Poster hero */}
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-400 mb-4 font-semibold" data-testid="poster-eyebrow">
              <Sparkle size={12} weight="fill" /> Digital Exam &amp; Certification Platform
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-4" data-testid="poster-title">
              Stop Issuing Paper.<br />
              <span className="text-amber-400">Start Issuing Trust.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl" data-testid="poster-subtitle">
              The all-in-one platform for modern coaching centers &amp; institutes.
            </p>
          </div>

          {/* Three feature strips */}
          <div className="grid md:grid-cols-3 gap-0 mt-14 border border-zinc-800">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className={`p-8 ${i < 2 ? "md:border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} border-zinc-800`} data-testid={f.testid}>
                  <div className="w-12 h-12 border border-amber-400/40 bg-amber-400/10 flex items-center justify-center mb-5">
                    <Icon size={22} weight="regular" className="text-amber-400" />
                  </div>
                  <div className="text-lg font-bold mb-2">{f.title}</div>
                  <div className="text-sm text-zinc-400 leading-relaxed">{f.body}</div>
                </div>
              );
            })}
          </div>

          {/* Pricing plans */}
          <div className="mt-16">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Simple pricing</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-10" data-testid="pricing-title">
              Pick the plan that fits your institute
            </h2>
            <div className="grid md:grid-cols-3 gap-0 border border-zinc-800">
              {PLANS.map((p, i) => (
                <div
                  key={p.key}
                  className={`relative p-8 ${i < 2 ? "md:border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} border-zinc-800 flex flex-col ${
                    p.featured ? "bg-amber-400 text-zinc-950" : ""
                  }`}
                  data-testid={`plan-${p.key}`}
                >
                  {p.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zinc-950 text-amber-400 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1 border border-amber-400 flex items-center gap-1" data-testid={`plan-${p.key}-badge`}>
                      <Star size={10} weight="fill" /> {p.badge}
                    </div>
                  )}
                  <div className={`text-[11px] uppercase tracking-[0.25em] font-semibold mb-3 ${p.featured ? "text-zinc-800" : "text-zinc-500"}`}>{p.name}</div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <div className={`font-display text-4xl sm:text-5xl font-bold tracking-tight ${p.featured ? "text-zinc-950" : ""}`} data-testid={`plan-${p.key}-price`}>{p.price}</div>
                    <div className={`text-sm ${p.featured ? "text-zinc-700" : "text-zinc-500"}`}>{p.period}</div>
                  </div>
                  <div className={`text-sm font-medium mb-6 ${p.featured ? "text-zinc-800" : "text-zinc-300"}`}>{p.students}</div>
                  <ul className={`space-y-2.5 mb-8 flex-1 ${p.featured ? "text-zinc-900" : "text-zinc-400"}`}>
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle size={16} weight="fill" className={p.featured ? "text-zinc-900 flex-shrink-0 mt-0.5" : "text-amber-400 flex-shrink-0 mt-0.5"} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={`https://wa.me/919154637357?text=${encodeURIComponent(`I would like to subscribe to the ${p.name} plan (${p.price}${p.period}).`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${
                      p.featured
                        ? "bg-zinc-950 text-amber-400 hover:bg-zinc-800"
                        : "border border-zinc-700 text-zinc-100 hover:border-amber-400 hover:text-amber-400"
                    }`}
                    data-testid={`plan-${p.key}-cta`}
                  >
                    {p.cta} <ArrowRight size={14} weight="bold" />
                  </a>
                </div>
              ))}
            </div>
            <div className="mt-6 text-xs text-zinc-500">
              All prices in INR. Contact us for annual billing discounts and custom institutional quotes.
            </div>
          </div>

          {/* Poster footer CTA */}
          <div className="mt-14 border-t border-zinc-800 pt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="font-display text-xl sm:text-2xl font-bold tracking-tight">Get started today at www.vexamos.com</div>
              <div className="text-sm text-zinc-400 mt-1">Modernizing education, one certificate at a time.</div>
            </div>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-amber-400 text-zinc-950 px-6 py-3 text-sm font-bold hover:bg-amber-300 transition-colors"
              data-testid="poster-cta-btn"
            >
              Start free trial <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ / Contact */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Need help choosing?</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">Talk to our team</h2>
          <p className="text-sm text-zinc-600 max-w-xl mb-8">
            Get a personalised walkthrough and pricing tailored to your student volume.
          </p>
          <div className="border border-zinc-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Contact</div>
              <div className="font-display text-2xl font-bold tracking-tight" data-testid="pricing-contact-number">+91 91546 37357</div>
              <div className="text-xs text-zinc-500 mt-1">Reach us on WhatsApp — replies within business hours.</div>
            </div>
            <a
              href="https://wa.me/919154637357?text=Pricing%20enquiry"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm font-semibold hover:bg-[#20BA5A] transition-colors"
              data-testid="pricing-whatsapp-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
              </svg>
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <footer className="py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-xs text-zinc-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>© {new Date().getFullYear()} ExamOS. Built for institutional rigor.</span>
            <span className="text-zinc-300">·</span>
            <Link to="/privacy" className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2" data-testid="pricing-footer-privacy">Privacy</Link>
            <span className="text-zinc-300">·</span>
            <Link to="/terms" className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2" data-testid="pricing-footer-terms">Terms</Link>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-mono">v1.0</span>
            <span className="text-zinc-300">·</span>
            <span>
              developed by{" "}
              <a href="https://vvtechnocrats.com/" target="_blank" rel="noreferrer" className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2" data-testid="pricing-footer-developer">
                @v3vidyavikas
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
